import React, { useEffect, useState } from "react";
import ResultsList from "../components/ResultsList";
import Select from "../components/Select";
import { searchAPI, RateLimitError } from "../services/api";
import { DOCUMENT_TYPE_OPTIONS } from "../types";
import type { SearchResult, RateLimitExceeded } from "../types";

const formatResetTime = (when: number | string): string => {
  const d = new Date(when);
  if (isNaN(d.getTime())) return "the next hour";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

// Format a number of seconds as mm:ss for the countdown.
const formatCountdown = (totalSeconds: number): string => {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

// ---------------------------------------------------------------------------
// Redirect configuration
// Update these values when City of Merced provides the portal URLs.
// ---------------------------------------------------------------------------
const REDIRECT_URL_NO_RESULTS = "https://mercedca.justfoia.com/publicportal/home/newrequest"; // TODO: Replace with document request portal URL
const REDIRECT_URL_PRE2010 = "https://mercedca.justfoia.com/publicportal/home/newrequest"; // TODO: Replace with historical records request URL

const NO_RESULTS_MESSAGE =
  "No public records were found matching your search criteria. " +
  "You may submit a formal document request through the City of Merced portal.";

const PRE2010_MESSAGE =
  "Documents dated before July 1, 2026 are not available through this public portal. " +
  "To request historical records, please submit a formal document request.";
// ---------------------------------------------------------------------------

const DocumentSearchPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 5; // Max 5 per proposal

  const [rateLimit, setRateLimit] = useState<RateLimitExceeded | null>(null);
  // Absolute wall-clock instant (epoch ms) when the hour window resets. Both the
  // "try again at" time and the countdown are derived from this single anchor, so
  // they can never drift apart or freeze past the reset time.
  const [resetAt, setResetAt] = useState<number | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const isRateLimited = rateLimit !== null;

  const [filters, setFilters] = useState({
    reference_number: undefined as string | undefined,
    related_party: undefined as string | undefined,
    subject_location: undefined as string | undefined,
    document_type: undefined as string | undefined,
    document_year: undefined as number | undefined,
  });

  // Debounce timer ref
  const debounceTimerRef = React.useRef<number | null>(null);

  // Debounced search when filters or search query change.
  // Any filter/query change always resets to the first page and searches it,
  // so changing the search while on page 2/3 doesn't leave stale results.
  useEffect(() => {
    // While rate-limited, don't fire searches (they'd just be rejected).
    if (isRateLimited) return;

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search (800ms delay)
    debounceTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      performSearch(1);
    }, 800);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [filters, searchQuery, isRateLimited]);

  // Countdown while rate-limited. Recomputed from the real wall clock each tick
  // (not prev-1), so it self-corrects if a tick is dropped or the tab is
  // backgrounded and always hits zero exactly at the reset instant. When it
  // reaches zero the hour window has reset, so we clear the limit and re-run the
  // current search automatically.
  useEffect(() => {
    if (!isRateLimited || resetAt === null) return;

    let tick: ReturnType<typeof setInterval> | undefined;
    const update = () => {
      const remaining = Math.max(0, Math.round((resetAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        if (tick) clearInterval(tick);
        setResetAt(null);
        setRateLimit(null); // re-enables inputs; debounce effect re-runs search
      }
    };

    update(); // sync immediately so the first render isn't a stale value
    tick = setInterval(update, 1000);

    return () => {
      if (tick) clearInterval(tick);
    };
  }, [isRateLimited, resetAt]);

  const performSearch = async (page: number = currentPage) => {
    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const response = await searchAPI.searchDocuments(
        {
          query: searchQuery || undefined,
          reference_number: filters.reference_number,
          related_party: filters.related_party,
          subject_location: filters.subject_location,
          document_type: filters.document_type,
          document_year: filters.document_year,
          page,
          page_size: pageSize,
        },
        false, // No need for facets with text inputs
      );

      setResults(response.results);
      setTotalCount(response.total_count);
      setTotalPages(response.total_pages);
    } catch (err) {
      if (err instanceof RateLimitError) {
        // Global hourly limit reached -> disable search + start the countdown.
        // Anchor the reset to an absolute wall-clock instant computed from the
        // server's retryAfterSeconds, then derive both the "try again at" time
        // and the live countdown from it so they always agree.
        const resetAtMs = Date.now() + err.info.retryAfterSeconds * 1000;
        setRateLimit(err.info);
        setResetAt(resetAtMs);
        setSecondsLeft(err.info.retryAfterSeconds);
        setError(null);
        setResults([]);
      } else {
        console.error("Search failed:", err);
        setError("Failed to search documents. Please try again.");
        setResults([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterName: string, value: string) => {
    const newFilters = { ...filters };
    if (filterName === "document_year") {
      (newFilters as any)[filterName] = value ? parseInt(value, 10) : undefined;
    } else {
      newFilters[filterName as keyof typeof filters] = (value ||
        undefined) as any;
    }
    setFilters(newFilters);
    // Search will be triggered automatically by useEffect
  };

  const handleClearAll = () => {
    setFilters({
      reference_number: undefined,
      related_party: undefined,
      subject_location: undefined,
      document_type: undefined,
      document_year: undefined,
    });
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    performSearch(page);
    // Scroll the results section to top
    const resultsSection = document.getElementById("results-container");
    if (resultsSection) {
      resultsSection.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const hasActiveFilters =
    searchQuery ||
    Object.values(filters).some(
      (val) => val !== undefined && val !== null && val !== "",
    );

  // Scenario B: user has explicitly filtered to a pre-2010 year
  const isPre2010Search = !!(filters.document_year && filters.document_year < 2010);

  return (
    <div className="h-[100vh] flex flex-col bg-merced-gray-50">
      {/* Skip link for keyboard / screen-reader users (WCAG 2.4.1) */}
      <a
        href="#results-container"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-merced-blue-700 focus:px-4 focus:py-2 focus:text-sm focus:text-white focus:shadow-lg"
      >
        Skip to results
      </a>

      {/* Thin government accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-merced-blue-700 via-merced-blue-500 to-merced-gold" />

      {/* Sticky City of Merced Header */}
      <header className="sticky top-0 z-50 border-b border-merced-gray-200 bg-white shadow-md animate-fade-in-down">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-3">
              <img
                src="https://www.cityofmerced.gov/Project/Contents/Main/_gfx/cmn/logo.svg"
                alt="City of Merced Logo"
                className="h-12 w-auto transition-transform duration-300 hover:scale-105 sm:h-14"
              />
              <div className="hidden border-l border-merced-gray-200 pl-3 sm:block">
                <p className="text-lg font-heading font-semibold leading-tight text-merced-blue-700">
                  Contracts and Resolutions Portal
                </p>
              </div>
            </div>
            {/* <div className="text-right">
              <h1 className="text-sm font-semibold text-merced-blue-700 sm:text-base">
                Public Document Search
              </h1>
              <p className="hidden text-xs text-merced-gray-500 sm:block">
                Search and download public records
              </p>
            </div> */}
          </div>
        </div>
      </header>

      {/* Search and Filters Section */}
      <div className="sticky top-0 z-40 bg-white shadow-md border-b border-merced-gray-200 animate-fade-in">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
          {/* Search and Filter Inputs in One Card */}
          <div className="relative rounded-lg border border-merced-gray-100 bg-white p-3 pl-4 space-y-3 shadow-sm before:absolute before:left-0 before:top-3 before:bottom-3 before:w-1 before:rounded-full before:bg-merced-gold before:content-['']">
            {/* First Row: Document Type, Contract Number, Agency, Subject, Calendar Year */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {/* Document Type filter */}
              <div>
                <label
                  htmlFor="document-type"
                  className="block text-xs font-medium text-merced-gray-700 mb-1"
                >
                  Document Type
                </label>
                <Select
                  value={filters.document_type || ""}
                  onChange={(value) =>
                    handleFilterChange("document_type", value)
                  }
                  options={DOCUMENT_TYPE_OPTIONS}
                  placeholder="All Types"
                  ariaLabel="Document Type"
                  className="w-full text-sm"
                  disabled={isRateLimited}
                />
              </div>

              <div>
                <label
                  htmlFor="contract-number"
                  className="block text-xs font-medium text-merced-gray-700 mb-1"
                >
                  Contract / Resolution No.
                </label>
                <div className="relative">
                  <input
                    id="contract-number"
                    type="text"
                    value={filters.reference_number || ""}
                    onChange={(e) =>
                      handleFilterChange("reference_number", e.target.value)
                    }
                    placeholder="Enter contract or resolution number..."
                    className="input-field text-sm w-full pr-8 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isRateLimited}
                  />
                  {filters.reference_number && (
                    <button
                      onClick={() => handleFilterChange("reference_number", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-merced-gray-400 hover:text-merced-gray-600 transition-colors"
                      aria-label="Clear reference number"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="outside-agency"
                  className="block text-xs font-medium text-merced-gray-700 mb-1"
                >
                  Agency/Party
                </label>
                <div className="relative">
                  <input
                    id="outside-agency"
                    type="text"
                    value={filters.related_party || ""}
                    onChange={(e) =>
                      handleFilterChange("related_party", e.target.value)
                    }
                    placeholder="Enter agency name..."
                    className="input-field text-sm w-full pr-8 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isRateLimited}
                  />
                  {filters.related_party && (
                    <button
                      onClick={() => handleFilterChange("related_party", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-merced-gray-400 hover:text-merced-gray-600 transition-colors"
                      aria-label="Clear agency"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="subject-location"
                  className="block text-xs font-medium text-merced-gray-700 mb-1"
                >
                  Subject/Location
                </label>
                <div className="relative">
                  <input
                    id="subject-location"
                    type="text"
                    value={filters.subject_location || ""}
                    onChange={(e) =>
                      handleFilterChange("subject_location", e.target.value)
                    }
                    placeholder="Enter subject or location..."
                    className="input-field text-sm w-full pr-8 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isRateLimited}
                  />
                  {filters.subject_location && (
                    <button
                      onClick={() => handleFilterChange("subject_location", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-merced-gray-400 hover:text-merced-gray-600 transition-colors"
                      aria-label="Clear subject/location"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Year filter */}
              <div>
                <label
                  htmlFor="document-year"
                  className="block text-xs font-medium text-merced-gray-700 mb-1"
                >
                  Calendar Year
                </label>
                <div className="relative">
                  <input
                    id="document-year"
                    type="number"
                    min="1970"
                    max={new Date().getFullYear() + 1}
                    value={filters.document_year ?? ""}
                    onChange={(e) =>
                      handleFilterChange("document_year", e.target.value)
                    }
                    placeholder="e.g. 2024"
                    className="input-field text-sm w-full pr-8 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isRateLimited}
                  />
                  {filters.document_year && (
                    <button
                      onClick={() => handleFilterChange("document_year", "")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-merced-gray-400 hover:text-merced-gray-600 transition-colors"
                      aria-label="Clear year"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Second Row: Keywords Search + Clear All Button */}
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1 w-full">
                <label
                  htmlFor="search-input"
                  className="block text-xs font-medium text-merced-gray-700 mb-1"
                >
                  Keywords Search
                </label>
                <div className="relative">
                  <input
                    id="search-input"
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by keywords or content..."
                    className="input-field text-sm w-full pr-8 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label="Search keywords"
                    disabled={isRateLimited}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-merced-gray-400 hover:text-merced-gray-600 transition-colors"
                      aria-label="Clear keywords"
                      type="button"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Clear All Button next to Keywords Search */}
              <div className="w-full sm:w-auto">
                <button
                  onClick={handleClearAll}
                  className="w-full sm:w-auto px-6 py-2 text-sm text-merced-blue-700 border border-merced-blue-700 rounded-md hover:bg-merced-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  aria-label="Clear all filters"
                  disabled={!hasActiveFilters || isRateLimited}
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 bg-merced-rust-50 border border-merced-rust-200 text-merced-rust-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Global hourly rate-limit notice (HTTP 429) */}
          {rateLimit && (
            <div
              role="alert"
              aria-live="assertive"
              className="mt-4 flex items-start gap-3 rounded-md border border-merced-gold-200 bg-merced-gold-50 px-4 py-3 text-sm text-merced-gray-800"
            >
              <svg
                className="mt-0.5 h-5 w-5 flex-shrink-0 text-merced-gold-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                />
              </svg>
              <div>
                <p className="font-semibold">Hourly search limit reached.</p>
                <p className="text-merced-gray-600">
                  Please try again at{" "}
                  {formatResetTime(resetAt ?? rateLimit.windowEndsAt)}.{" "}
                  <span className="font-medium text-merced-gray-800">
                    Search re-enables in {formatCountdown(secondsLeft)}.
                  </span>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content with Scrollable Results */}
      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-6 overflow-hidden">
        <div
          id="results-container"
          className="h-full overflow-y-auto pr-2 scroll-smooth animate-fade-in-up"
        >
          {isPre2010Search && hasSearched && !loading ? (
            /* Scenario B — pre-2010 redirect notice */
            <div className="flex flex-col items-center justify-center py-14 px-6 bg-white rounded-lg shadow text-center space-y-5 animate-scale-in">
              <div className="w-14 h-14 bg-merced-gold-50 rounded-full flex items-center justify-center">
                <svg className="w-7 h-7 text-merced-gold-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base font-semibold text-merced-gray-800 mb-2">Historical Records Request</h3>
                <p className="text-sm text-merced-gray-500 max-w-sm">{PRE2010_MESSAGE}</p>
              </div>
              <a
                href={REDIRECT_URL_PRE2010}
                className="group inline-flex items-center gap-2 px-5 py-2.5 bg-merced-blue-700 text-white text-sm font-medium rounded-md hover:bg-merced-blue-800 hover:shadow-md transition-all duration-200"
                target="_blank"
                rel="noopener noreferrer"
              >
                Request Historical Document
                <svg className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          ) : (
            /* Scenario A — normal results (no-results state handled inside ResultsList) */
            <ResultsList
              results={results}
              loading={loading}
              totalCount={totalCount}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              hasSearched={hasSearched}
              noResultsUrl={REDIRECT_URL_NO_RESULTS}
              noResultsMessage={NO_RESULTS_MESSAGE}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-auto bg-merced-blue-700 text-white">
        <div className="h-1 w-full bg-gradient-to-r from-merced-gold via-merced-blue-500 to-merced-blue-700" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-sm text-center sm:text-left">
              © {new Date().getFullYear()} City of Merced, California. All
              rights reserved.
            </p>
            <p className="text-xs text-merced-blue-200 text-center sm:text-right">
              678 W. 18th Street, Merced, CA 95340 |{" "}
              <a
                href="https://www.cityofmerced.gov"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline transition-colors hover:text-white"
              >
                www.cityofmerced.gov
              </a>
              {" "} | {" "}
              <a
                href="tel:+12093888650"
                className="link-underline transition-colors hover:text-white"
              >
                Tel: (209) 388-8650
              </a>{" "}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocumentSearchPage;
